import { Injectable } from '@angular/core';
import { BaseApiService, Result, ResultType, aaDataResponse } from '@shared/data-access';
import { BaseListOfficerService, OfficerBaseResponse, OfficerResponse } from '@shared/feature/list-officer';
import { TableData, TableDataResponse } from '@shared/ui/tables';
import { Observable, first, map, of, switchMap } from 'rxjs';
import { IntrensicOfficerResponse, OfficerDetailFacade } from '@intrensic/shared/officer-details';
import { UserRole } from '@shared/utils/base-auth';

@Injectable({
  providedIn: 'root',
})
export class IntrensicListOfficersService extends BaseListOfficerService {
  constructor(api: BaseApiService, private currentOfficerFacade: OfficerDetailFacade) {
    super(api);
  }

  override list(query: string): Observable<Result<TableDataResponse<OfficerResponse>>> {
    return this.currentOfficerFacade.getLoggedInOfficer().pipe(
      first(),
      switchMap((loggedInOfficer) => {
        const role = loggedInOfficer.role;

        switch (role) {
          case UserRole.Supervisor:
          case UserRole.Commander:
          case UserRole.HRAdmin:
          case UserRole.SuperAdmin:
            return this.api.get<aaDataResponse<IntrensicOfficerResponse>>(`api/users/odata?${query}`).pipe(
              map((response) => {
                const tableData = new TableData(
                  response.iTotalRecords,
                  response.aaData.map(
                    (values) =>
                      new OfficerResponse(
                        values.UID,
                        values.FirstName,
                        values.LastName,
                        null,
                        values.EmailAddress,
                        values.TimeZone,
                        values.EmployeeID,
                        values.PictureFullPath,
                        values.InviteSent,
                        values.AccountType,
                        values.Phone,
                        values.Role,
                        values.ShareType,
                        values.RespondTo != null
                          ? new OfficerBaseResponse(
                              values.RespondTo.UID,
                              values.RespondTo.FirstName,
                              values.RespondTo.LastName,
                              null,
                              values.RespondTo.EmailAddress,
                              values.RespondTo.TimeZone,
                              values.RespondTo.EmployeeID,
                              values.RespondTo.PictureFullPath,
                              values.InviteSent,
                              values.AccountType
                            )
                          : null,
                        values.Children
                      )
                  )
                );

                return {
                  value: tableData,
                  isSuccess: true,
                  isFailure: false,
                  isEmpty: false,
                  message: '',
                  httpStatusCode: 200,
                  resultType: ResultType.Ok,
                };
              })
            );

          default:
            return of({
              value: new TableData(0, []),
              isSuccess: true,
              isFailure: false,
              isEmpty: false,
              message: '',
              httpStatusCode: 200,
              resultType: ResultType.Ok,
            });
        }
      })
    );
  }

  override deleteOfficer(uid: string): Observable<string> {
    return this.api.delete(`api/users/${uid}`);
  }

  override inviteUser(uid: string): Observable<string> {
    return this.api.post(`api/users/${uid}/invite`);
  }
}
